/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useMemo } from 'react';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import {
  useCertonaInventoryOptions,
  type CertonaInventoryResponse,
} from './useCertonaInventoryOptions';

const FULFILLMENT_PRIORITY = {
  BOPUS: FULFILLMENT_METHODS.STOREORDER,
  STH: FULFILLMENT_METHODS.ONLINEORDER,
} as const;

export type SkuAddToCartConfig = {
  showAddToCart: boolean;
  fulfillmentId: number | null;
  availableQty: number;
  bopusAvailable: boolean;
  sthAvailable: boolean;
};

export type CertonaAddToCartVisibilityResult = {
  isLoading: boolean;
  isError: boolean;
  isFeatureEnabled: boolean;
  skuConfigs: Record<string, SkuAddToCartConfig>;
  shouldShowAddToCart: (skuId: string) => boolean;
  getFulfillmentId: (skuId: string) => number | null;
  inventoryData: CertonaInventoryResponse | undefined;
};

function getSkuAddToCartConfig(
  inventoryOptions: Array<{ available: boolean; fulfillmentId: number; availableQty: number }> = [],
): SkuAddToCartConfig {
  const bopusOption = inventoryOptions.find(
    (opt) => opt.fulfillmentId === FULFILLMENT_PRIORITY.BOPUS,
  );
  const bopusAvailable = !!(bopusOption?.available && bopusOption.availableQty > 0);

  const sthOption = inventoryOptions.find((opt) => opt.fulfillmentId === FULFILLMENT_PRIORITY.STH);
  const sthAvailable = !!(sthOption?.available && sthOption.availableQty > 0);

  let fulfillmentId: number | null = null;
  let availableQty = 0;

  if (bopusAvailable) {
    fulfillmentId = FULFILLMENT_PRIORITY.BOPUS;
    availableQty = bopusOption?.availableQty ?? 0;
  } else if (sthAvailable) {
    fulfillmentId = FULFILLMENT_PRIORITY.STH;
    availableQty = sthOption?.availableQty ?? 0;
  }

  const showAddToCart = bopusAvailable || sthAvailable;

  return {
    showAddToCart,
    fulfillmentId,
    availableQty,
    bopusAvailable,
    sthAvailable,
  };
}

export const useCertonaAddToCartVisibility = (
  skuIds: string[],
): CertonaAddToCartVisibilityResult => {
  const isFeatureEnabled = useCertonaAddToCartFlag();

  const {
    data: inventoryData,
    isLoading,
    isError,
  } = useCertonaInventoryOptions(isFeatureEnabled && skuIds.length > 0 ? skuIds : []);

  const skuConfigs = useMemo(() => {
    if (!inventoryData?.inventory) {
      return {};
    }

    const configs: Record<string, SkuAddToCartConfig> = {};

    for (const skuId of skuIds) {
      const inventoryOptions = inventoryData.inventory[skuId] ?? [];
      configs[skuId] = getSkuAddToCartConfig(inventoryOptions);
    }

    return configs;
  }, [inventoryData, skuIds]);

  const shouldShowAddToCart = (skuId: string): boolean => {
    if (!isFeatureEnabled) {
      return true;
    }

    if (isLoading || isError) {
      return false;
    }

    return skuConfigs[skuId]?.showAddToCart ?? false;
  };

  const getFulfillmentId = (skuId: string): number | null => {
    return skuConfigs[skuId]?.fulfillmentId ?? null;
  };

  return {
    isLoading: isFeatureEnabled ? isLoading : false,
    isError: isFeatureEnabled ? isError : false,
    isFeatureEnabled,
    skuConfigs,
    shouldShowAddToCart,
    getFulfillmentId,
    inventoryData,
  };
};

export const useSingleSkuAddToCartVisibility = (skuId: string | undefined) => {
  const skuIds = useMemo(() => (skuId ? [skuId] : []), [skuId]);
  const result = useCertonaAddToCartVisibility(skuIds);

  return {
    showAddToCart: skuId ? result.shouldShowAddToCart(skuId) : false,
    fulfillmentId: skuId ? result.getFulfillmentId(skuId) : null,
    config: skuId ? result.skuConfigs[skuId] : undefined,
    isLoading: result.isLoading,
    isError: result.isError,
    isFeatureEnabled: result.isFeatureEnabled,
  };
};
